// src/api.ts — Browser-side API helper for Torrentio + Real-Debrid

// ==================== RD Key Management ====================

const RD_KEY_STORAGE = "rd_api_key";

export function getRdApiKey(): string | null {
  return localStorage.getItem(RD_KEY_STORAGE);
}

export function setRdApiKey(key: string): void {
  localStorage.setItem(RD_KEY_STORAGE, key.trim());
}

export function clearRdApiKey(): void {
  localStorage.removeItem(RD_KEY_STORAGE);
}

// ==================== Cinemeta API ====================

export interface CinemetaMeta {
  id: string;
  imdb_id: string;
  type: string;
  name: string;
  poster?: string;
  releaseInfo?: string;
}

export interface CinemetaVideo {
  id: string;
  season: number;
  number: number;
  name: string;
  released?: string;
  overview?: string;
  thumbnail?: string;
}

export interface CinemetaSeriesMeta extends CinemetaMeta {
  videos: CinemetaVideo[];
}

export async function searchCinemeta(
  query: string,
  type: "movie" | "series" = "movie"
): Promise<CinemetaMeta[]> {
  const encoded = encodeURIComponent(query);
  const res = await fetch(
    `https://v3-cinemeta.strem.io/catalog/${type}/top/search=${encoded}.json`
  );
  if (!res.ok)
    throw new Error(`Cinemeta error: ${res.status} ${res.statusText}`);
  const data = await res.json();
  return data.metas ?? [];
}

export async function getSeriesMeta(
  imdbId: string
): Promise<CinemetaSeriesMeta> {
  const res = await fetch(
    `https://v3-cinemeta.strem.io/meta/series/${imdbId}.json`
  );
  if (!res.ok)
    throw new Error(`Cinemeta error: ${res.status} ${res.statusText}`);
  const data = await res.json();
  return data.meta;
}

// ==================== Types ====================

export interface TorrentStream {
  name: string;
  title: string;
  infoHash?: string;
  fileIdx?: number;
  url?: string;
  behaviorHints?: Record<string, unknown>;
}

interface TorrentioResponse {
  streams: TorrentStream[];
}

// ==================== Torrentio API ====================

const TORRENTIO_OPTIONS = [
  "sort=seeders",
  "limit=100",
  "qualityfilter=4k,threed,scr,cam,unknown",
].join("|");

function buildTorrentioBaseUrl(): string {
  const rdKey = getRdApiKey();
  if (!rdKey) throw new Error("Real-Debrid API key not set");
  return `https://torrentio.strem.fun/${TORRENTIO_OPTIONS}|realdebrid=${rdKey}`;
}

/** Extract infoHash and fileIdx from RD resolve URL */
function parseRdUrl(url: string): {
  infoHash: string | undefined;
  fileIdx: number | undefined;
} {
  const parts = url.split("/");
  const hashIdx = parts.findIndex((p) => p === "realdebrid") + 2;
  const rawFileIdx = parts[hashIdx + 2];
  return {
    infoHash: parts[hashIdx],
    fileIdx:
      rawFileIdx && rawFileIdx !== "null" ? Number(rawFileIdx) : undefined,
  };
}

function mapStreams(streams: TorrentStream[]): TorrentStream[] {
  return streams.map((s) => {
    if (s.url) {
      const { infoHash, fileIdx } = parseRdUrl(s.url);
      return { ...s, infoHash, fileIdx };
    }
    return s;
  });
}

export async function getMovieTorrents(
  imdbId: string
): Promise<TorrentStream[]> {
  const base = buildTorrentioBaseUrl();
  const res = await fetch(`${base}/stream/movie/${imdbId}.json`);
  if (!res.ok)
    throw new Error(`Torrentio error: ${res.status} ${res.statusText}`);
  const data = (await res.json()) as TorrentioResponse;
  return mapStreams(data.streams);
}

export async function getSeriesTorrents(
  imdbId: string,
  season: number,
  episode: number
): Promise<TorrentStream[]> {
  const base = buildTorrentioBaseUrl();
  const res = await fetch(
    `${base}/stream/series/${imdbId}:${season}:${episode}.json`
  );
  if (!res.ok)
    throw new Error(`Torrentio error: ${res.status} ${res.statusText}`);
  const data = (await res.json()) as TorrentioResponse;
  return mapStreams(data.streams);
}

// ==================== Watch History ====================

export interface HistoryEntry {
  url: string;
  title: string;
  positionMs: number;
  durationMs: number;
  timestamp: number; // Date.now()
}

const HISTORY_KEY = "watch_history";
const HISTORY_ENABLED_KEY = "history_enabled";

export function getHistory(): HistoryEntry[] {
  const raw = localStorage.getItem(HISTORY_KEY);
  return raw ? JSON.parse(raw) : [];
}

export function updateHistory(entry: Omit<HistoryEntry, "timestamp">): void {
  if (!isHistoryEnabled()) return;
  if (!entry.url) return;
  const history = getHistory();
  const idx = history.findIndex((h) => h.url === entry.url);
  const updated: HistoryEntry = { ...entry, timestamp: Date.now() };
  if (idx >= 0) history[idx] = updated;
  else history.push(updated);
  localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
}

export function clearHistory(): void {
  localStorage.removeItem(HISTORY_KEY);
}

export function isHistoryEnabled(): boolean {
  return localStorage.getItem(HISTORY_ENABLED_KEY) !== "false";
}

export function setHistoryEnabled(enabled: boolean): void {
  localStorage.setItem(HISTORY_ENABLED_KEY, String(enabled));
}

// ==================== Internet Archive API ====================

const SAFE_COLLECTIONS = [
  "opensource_movies",
  "prelinger",
  "feature_films",
  "classic_tv",
  "short_films",
  "silent_films",
  "film_noir",
  "scifi",
  "comedy_films",
  "horror_movies",
] as const;

const COLLECTION_FILTER = SAFE_COLLECTIONS.map((c) => `collection:${c}`).join(
  " OR "
);

const VIDEO_EXTENSIONS = [".mp4", ".mkv", ".avi", ".ogv", ".webm"];

export interface ArchiveSearchResult {
  identifier: string;
  title: string;
  description?: string;
  year?: string;
  creator?: string;
  mediatype: string;
  downloads?: number;
}

export interface ArchiveFile {
  name: string;
  title?: string;
  format: string;
  size: string;
  length?: string;
  url: string;
}

export async function searchArchive(
  query: string,
  page = 1,
  rows = 25
): Promise<{ results: ArchiveSearchResult[]; total: number }> {
  const q = encodeURIComponent(
    `(${query}) AND (${COLLECTION_FILTER}) AND mediatype:movies`
  );
  const fields = "identifier,title,description,year,creator,mediatype,downloads";
  const url = `https://archive.org/advancedsearch.php?q=${q}&fl[]=${fields}&sort[]=downloads+desc&rows=${rows}&page=${page}&output=json`;

  const res = await fetch(url);
  if (!res.ok) throw new Error(`Archive.org error: ${res.status}`);
  const data = await res.json();

  return {
    results: data.response?.docs ?? [],
    total: data.response?.numFound ?? 0,
  };
}

export async function getArchiveFiles(
  identifier: string
): Promise<ArchiveFile[]> {
  const res = await fetch(`https://archive.org/metadata/${identifier}/files`);
  if (!res.ok) throw new Error(`Archive.org error: ${res.status}`);
  const data = await res.json();

  const baseUrl = `https://archive.org/download/${identifier}`;

  return (data.result ?? [])
    .filter((f: any) => {
      const name = (f.name as string).toLowerCase();
      return VIDEO_EXTENSIONS.some((ext) => name.endsWith(ext));
    })
    .map((f: any) => ({
      name: f.name,
      title: f.title || f.name,
      format: f.format || "",
      size: f.size || "0",
      length: f.length,
      url: `${baseUrl}/${encodeURIComponent(f.name)}`,
    }))
    .sort((a: ArchiveFile, b: ArchiveFile) => {
      return parseInt(b.size) - parseInt(a.size);
    });
}

export function formatFileSize(bytes: string | number): string {
  const b = typeof bytes === "string" ? parseInt(bytes) : bytes;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export function formatDuration(seconds: string | number | undefined): string {
  if (!seconds) return "";
  const s = typeof seconds === "string" ? parseFloat(seconds) : seconds;
  if (isNaN(s)) return "";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  return `${m}:${String(sec).padStart(2, "0")}`;
}

